import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useField } from "formik";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { selectDarkMode } from "../../../app/store/slices/uiSlice";
import { FormInput, FormSelect, FormTextarea, FormToggle } from "../ui/form";
import { ProfileUpload } from "../../../features/users/components/profileUpload";
import { RichTextEditor } from "../ui/RichTextEditor";
import Tooltip from "../ui/Tooltip";
import InfiniteSearchSelect from "../ui/form/InfiniteSearchSelect";

export default function CRUDFormikField({ field, formikProps, customHandlers }) {
  const darkMode = useSelector(selectDarkMode);
  const [options, setOptions] = useState(field.options || []);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Use Formik's useField hook for field-level state
  const [formikField, meta, helpers] = useField(field.name);
  const { values, setFieldValue, setValues } = formikProps;

  // Sync options from props when they change (dynamic options support)
  useEffect(() => {
    if (!field.optionsLoader && Array.isArray(field.options)) {
      setOptions(field.options);
    }
  }, [field.options, field.optionsLoader]);

  useEffect(() => {
    if (field.optionsLoader && typeof field.optionsLoader === "function") {
      setLoadingOptions(true);
      field
        .optionsLoader(values)
        .then((loadedOptions) => {
          setOptions(loadedOptions || []);
          setLoadingOptions(false);
        })
        .catch((err) => {
          console.error("Error loading options:", err);
          setLoadingOptions(false);
        });
    }
  }, [field.optionsLoader, values?.parentId, field.name]);

  if (field.type === "multi-append") {
    const selectedValues = values[field.name] || [];

    return (
      <div>
        {/* Label + Tooltip */}
        <div className="flex items-center mb-1">
          <label className="block">{field.label}</label>

          {field.tooltip && (
            <div className="ml-2">
              <Tooltip content={field.tooltip} position="top">
                <InformationCircleIcon className="w-4 h-4 text-gray-500 cursor-help" />
              </Tooltip>
            </div>
          )}
        </div>

        {/* Chips */}
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedValues.map((item, index) => (
            <span key={`${item}-${index}`} className="flex items-center bg-indigo-100 rounded-full px-3 py-1 text-sm">
              {item}
              <button
                type="button"
                className="ml-2 font-bold"
                onClick={() =>
                  setFieldValue(
                    field.name,
                    selectedValues.filter((v) => v !== item),
                  )
                }
              >
                ×
              </button>
            </span>
          ))}
        </div>

        {/* Text Area */}
        <textarea
          rows={2}
          placeholder={`Type ${field.label} and press Enter`}
          className="w-full border rounded px-2 py-1"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();

              const value = e.target.value.trim();

              if (!value) return;
              if (selectedValues.includes(value)) return;

              setFieldValue(field.name, [...selectedValues, value]);
              e.target.value = "";
            }
          }}
        />
      </div>
    );
  }

  if (field.type === "multi-select") {
    const selectedValues = values[field.name] || [];

    return (
      <div>
        <label className="block mb-1">{field.label}</label>

        {/* Chips */}
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedValues.map((item) => {
            const label = options.find((o) => o.value === item)?.label || item;

            return (
              <span key={item} className="flex items-center bg-indigo-100 rounded-full px-3 py-1 text-sm">
                {label}
                <button
                  type="button"
                  className="ml-2 font-bold"
                  onClick={() =>
                    setFieldValue(
                      field.name,
                      selectedValues.filter((v) => v !== item),
                    )
                  }
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>

        {/* Dropdown */}
        <select
          value=""
          disabled={loadingOptions || options.length === 0}
          onChange={(e) => {
            const value = e.target.value;
            if (!selectedValues.includes(value)) {
              setFieldValue(field.name, [...selectedValues, value]);
            }
          }}
          className="w-full border rounded px-2 py-1"
        >
          <option value="" disabled>
            {loadingOptions ? "Loading..." : options.length === 0 ? "No data available" : `Select ${field.label}`}
          </option>

          {options
            .filter((opt) => !selectedValues.includes(opt.value))
            .map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
        </select>
      </div>
    );
  }

  // Handle file upload / image fields (profilePicture by name, or any field with type="file")
  if (field.name === "profilePicture" || ["file", "image"].includes(field.type)) {
    return (
      <div key={field.name} className="mb-4 flex justify-center">
        <ProfileUpload field={field} value={formikField.value} onChange={(name, value) => setFieldValue(name, value)} error={meta.touched && meta.error ? meta.error : null} />
      </div>
    );
  }

  const hasError = meta.touched && meta.error;
  const isValid = meta.touched && !meta.error && formikField.value;

  const baseClass = "w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2";
  const borderClass = hasError
    ? "border-red-500 focus:ring-red-500"
    : isValid
      ? darkMode
        ? "border-green-500 focus:ring-green-500 bg-gray-700 text-white"
        : "border-green-500 focus:ring-green-500"
      : darkMode
        ? "border-gray-600 focus:ring-blue-500 bg-gray-700 text-white placeholder-gray-400"
        : "border-gray-300 focus:ring-blue-500";

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Check for custom onFieldChange handler (for auto-calculations)
    if (customHandlers?.onFieldChange) {
      const handled = customHandlers.onFieldChange(name, value, values, setValues);
      if (handled) return;
    }

    setFieldValue(name, value);
  };

  const isDisabled = typeof field.disabled === "function" ? field.disabled(values) : field.disabled;
  const isReadOnly = typeof field.readOnly === "function" ? field.readOnly(values) : field.readOnly;
  const isRequired = field.required || (field.requiredWhen && typeof field.requiredWhen === 'function' && field.requiredWhen(values));
  const normalizedValue = field.type === "date" && typeof formikField.value === "string" ? formikField.value.split("T")[0] : formikField.value === 0 ? 0 : formikField.value || "";

  const commonProps = {
    label: field.label,
    name: field.name,
    value: normalizedValue,
    onChange: handleChange,
    onBlur: formikField.onBlur,
    placeholder: field.placeholder,
    disabled: isDisabled,
    readOnly: isReadOnly,
    min: field.min,
    max: field.max,
    className: `${baseClass} ${borderClass} ${isReadOnly ? "bg-gray-100 cursor-not-allowed" : ""} ${darkMode && isReadOnly ? "!bg-gray-600" : ""}`,
  };

  let inputElement;
  switch (field.type) {
    // case "select":
    //   inputElement = <FormSelect key={field.name} {...commonProps} options={options} loading={loadingOptions} />;
    //   break;
    case "select":
      inputElement = (
        <div>
          <select
            name={field.name}
            value={normalizedValue || ""}
            onChange={(e) => {
              const selectedValue = e.target.value;
              // console.log("🎯 Select changed - selected value:", selectedValue);
              setFieldValue(field.name, selectedValue);
            }}
            onBlur={formikField.onBlur}
            disabled={isDisabled || loadingOptions}
            className={`${baseClass} ${borderClass} ${isReadOnly ? "bg-gray-100 cursor-not-allowed" : ""} ${darkMode && isReadOnly ? "!bg-gray-600" : ""}`}
          >
            <option value="" disabled>
              {loadingOptions ? "Loading..." : options.length === 0 ? "No data available" : field.placeholder || `Select ${field.label}`}
            </option>
            {options.map((opt) => (
              <option key={opt.value || opt.key} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      );
      break;
    case "infinite-select":
      inputElement = (
        <InfiniteSearchSelect
          name={field.name}
          value={formikField.value || ""}
          onChange={(e) => setFieldValue(field.name, e.target.value)}
          fetchFn={field.fetchFn}
          getOptions={field.getOptions}
          getTotal={field.getTotal}
          extraParams={field.extraParams || {}}
          disabled={isDisabled}
          placeholder={field.placeholder}
        />
      );
      break;
    case "textarea":
      inputElement = <FormTextarea key={field.name} {...commonProps} rows={field.rows || 4} />;
      break;
    case "richtext":
      inputElement = <RichTextEditor key={field.name} {...commonProps} onChange={(e) => setFieldValue(field.name, e.target.value)} />;
      break;
    case "checkbox":
      // Checkbox with toggle switch UI
      return (
        <FormToggle
          key={field.name}
          label={field.label}
          name={field.name}
          checked={!!formikField.value}
          onChange={(e) => setFieldValue(field.name, e.target.checked)}
          onBlur={formikField.onBlur}
          disabled={isDisabled}
          required={isRequired}
          error={hasError ? meta.error : ""}
          className="mb-3"
          // Confirmation modal props
          requireConfirmation={field.requireConfirmation}
          confirmTitle={field.confirmTitle}
          confirmMessage={field.confirmMessage}
          confirmLabel={field.confirmLabel}
          cancelLabel={field.cancelLabel}
        />
      );
    case "number":
      inputElement = <FormInput key={field.name} {...commonProps} type="number" min={field.min} max={field.max} step={field.step} />;
      break;
    default:
      inputElement = <FormInput key={field.name} {...commonProps} type={field.type || "text"} />;
  }

  return (
    <div key={field.name} className="mb-3">
      {field.label && (
        <div className="flex items-center mb-1">
          <label className={`block text-sm font-medium ${darkMode ? "text-gray-200" : "text-[#232526]"}`}>
            {field.label}
            {isRequired && <span className="text-red-500"> *</span>}
          </label>
          {field.tooltip && (
            <div className="ml-2">
              <Tooltip content={field.tooltip} position="top">
                <InformationCircleIcon className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"} cursor-help`} />
              </Tooltip>
            </div>
          )}
        </div>
      )}

      {inputElement}

      {hasError && <p className="text-red-500 text-sm mt-1">{meta.error}</p>}
    </div>
  );
}
